<template>
  <div :style="wrapperStyle" @mouseenter="onEnter" @mouseleave="hovered = false">
    <jd-button
      type="text"
      :disabled="disabled"
      :style="buttonInnerStyle"
      @click="onClick"
    >
      <template #icon>
        <component :is="icon" :style="iconStyle" />
      </template>
    </jd-button>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, inject, type CSSProperties } from 'vue'
import { AngleLeft, AngleRight } from '@jd/jdesign-icons-vue'
import { JdButton } from '@jdesign-vue/components/button'
import { useNamespace } from '@jdesign-vue/hooks'
import { statisticGroupKey } from '../tokens'
import { createColorVars } from '../constants'
import type { StatisticCardSize } from '../types'

const props = defineProps<{
  direction: 'prev' | 'next'
  disabled: boolean
  size: StatisticCardSize
}>()

const emit = defineEmits<{ (e: 'click'): void }>()

const hovered = ref(false)
const groupCtx = inject(statisticGroupKey, { inGroup: false })
const ns = useNamespace('statistic-card')
const colorVars = createColorVars(ns.cssVarName)

const icon = computed(() => props.direction === 'prev' ? AngleLeft : AngleRight)

const iconColor = computed(() => {
  if (props.disabled) return colorVars.buttonIconDisabled
  return hovered.value ? colorVars.buttonIconHover : colorVars.buttonIconDefault
})

const iconStyle = computed<CSSProperties>(() => ({
  color: iconColor.value,
  fontSize: '12px',
  width: '12px',
  height: '12px',
}))

// 外层容器承载背景/尺寸/圆角：避免 jd-button 的 .is-text.is-disabled { background: transparent }
// 在最后一页（禁用态）把背景抹掉，从而与 React 端保持一致的灰色圆角底框。
const wrapperStyle = computed<CSSProperties>(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  minWidth: '20px',
  height: props.size === 'large' ? '48px' : '40px',
  minHeight: props.size === 'large' ? '48px' : '40px',
  borderRadius: colorVars.radiusXs,
  background: groupCtx.inGroup ? colorVars.buttonWhiteBg : colorVars.buttonGrayBg,
  overflow: 'hidden',
}))

// 内部按钮透明填满外层容器，仅承载点击/图标
const buttonInnerStyle = computed<CSSProperties>(() => ({
  width: '100%',
  minWidth: '100%',
  height: '100%',
  minHeight: '100%',
  padding: '0',
  background: 'transparent',
  border: 'none',
}))

function onClick() { emit('click') }
function onEnter() { if (!props.disabled) hovered.value = true }
</script>
